import React from "react";

import Signal from "../models/Signal";
import Channel, { ChannelDescriptionProperty, ChannelTypeProperty } from "../models/Channel";
import { ChannelType } from "../models/ChannelType";

import { firstChildWithTag, getId, getParamRefId, getText, Tag } from "./XmlUtils";

import { ValueLevels } from "../models/KnxInstanceData";

import Device from "../models/Device";

import SignalTypes from "../models/SignalTypes";

import KnxProductData from "../KnxProductdata";
import ButtonChannel, { KnxSceneNumberProperty } from "../models/ButtonChannel";
import Logger from "../Logger";
import { isUiModeDeveloper, isUiModeProductAdmin } from "../uiMode";
import KnxParameterCalculation from "../models/KnxParameterCalculation";
import transpileVbScriptToJavaScript from "../transpiler/transpileVbScriptToJavaScript";
import { showDebugErrToast, showErrToast } from "./Toasts";
import { itx, noItx } from "../translationItx/itx";
import KnxChapterHierachie, { findChapterWithProperty } from "./KnxParameterViewer/KnxChapterHierachie";
import { KnxChapterItem } from "./KnxParameterViewer/KnxChapterItem";
import KnxContext from "./KnxParameterViewer/KnxContext";
import ParameterRefRef from "./KnxParameterViewer/ParameterRefRef";
import KnxParameterSeparatror from "./KnxParameterViewer/KnxParameterSeparator";
import Allocator from "./KnxParameterViewer/Allocator";
import TableInfo from "./KnxParameterViewer/KnxTableInfo";
import ComObjectRefRef, { calcValueWithBaseValue } from "./KnxParameterViewer/ComObjectRefRef";
import KnxButton from "./KnxParameterViewer/KnxButton";
import { isDevelopmentEnvironment } from "../estada-config";

const logger = new Logger();

////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////

type FlatItem = {
  name: string;
  indent: number;
  order: number;
  elm: any;
  type: "P" | "C" | "S" | "G";
  id: string;
  knxChapterItem?: KnxChapterItem;
};

export default async function KnxParameterViewer(props: {
  device: Device;
  isActor: boolean;
  xchannels: Array<Channel>;
  xSignals: Array<Signal>;
  isLandscape: boolean;
}): Promise<JSX.Element | null | undefined> {
  const { device, xchannels, xSignals, isLandscape } = props;

  let mainKnxChapters: Array<KnxChapterItem> = [];

  device.knxChapters = mainKnxChapters;

  let flatItems = new Array<FlatItem>();

  const parameterBlockRenames = new Map<string, any>();

  let hasAnyChannel = false;

  var lastParameterBlockChannel: any | undefined = undefined;

  let currentTable: TableInfo | undefined = undefined;

  let knxInstanceData = device.knxInstanceData;

  if (device.template == undefined) {
    return;
  }

  const knxProductData: KnxProductData = device.template.knxProductData;
  const knxContext = new KnxContext(knxProductData, knxInstanceData, device, props.isActor, isLandscape);
  knxContext.xSignals = xSignals;
  knxContext.flatItems = flatItems;
  knxContext.currentTable = currentTable;

  ////////////////////////////////////////////////////////////////////////////
  var channelIndex = 1;

  function createKnxChannel(parentChannel: Channel | undefined, data: any): Channel {
    var sIndex = `${channelIndex++}`;
    if (sIndex.length == 1) {
      sIndex = `0` + sIndex;
    }

    var channel: Channel;
    data.tag = sIndex;
    data.isRequestChannel = false;

    if (knxContext.isActor) {
      channel = new Channel(data, device);
    } else {
      channel = new ButtonChannel(data, device);
    }

    if (parentChannel && parentChannel.channelType != ChannelType.General) {
      if (!parentChannel.subChannels) {
        parentChannel.subChannels = new Array<Channel>();
      }
      parentChannel.subChannels.push(channel);
    } else {
      xchannels.push(channel);
    }

    return channel;
  }
  /////////////////////////////////

  //

  ////////////////////////////////////////////////////////////////////////////

  var data = device.getApplicationProgram();
  if (data == undefined) {
    logger.error("data == undefinded");
    return null;
  }

  const Static = firstChildWithTag(data, "Static"); // no itx

  Allocator.allocators = []; // Clear allocators

  const xAllocators = firstChildWithTag(Static, "Allocators"); // no itx
  for (let i in xAllocators?.items) {
    const allocator = xAllocators.items[i];
    Allocator.allocators.push(new Allocator(allocator.a));
  }
  knxContext.setAllocators(Allocator.allocators);

  if (!knxProductData.knxScriptCode) {
    const Script = firstChildWithTag(Static, "Script"); // no itx
    if (Script) {
      const scriptCode = Script._;
      if (scriptCode) {
        knxProductData.knxScriptCode = scriptCode;
      }
    }
  }

  knxInstanceData.stripPrefix(knxProductData.prefix);
  knxProductData.languageMapping.reload(state.settings.language);

  ////////////////////////////////////////////////////////////////////////////////////////

  ////////////////////////////////////////////////////////////////////////////

  function findParentChannelByIndent(
    xchannels: Array<Channel>,
    parentChannel: Channel,
    firstNonWhiteSpaceChar: number,
  ): Channel {
    let res = parentChannel;

    let i = xchannels.length - 1;
    let xParent: Channel | undefined;
    while (i >= 0 && !xParent) {
      const c = xchannels[i--];
      if (c.name.includes("DUMMY")) {
        continue;
      }
      if (c.subChannelIndent < firstNonWhiteSpaceChar) {
        xParent = c;
      }
    }

    if (xParent) {
      return xParent;
    } else {
      return parentChannel;
    }
  }

  function ParameterBlock(props: {
    title: string;
    nestedName: string;
    nestedText: string;
    parentChannel: Channel;
    xchannels: Array<Channel>;
    data: any;
    args: any;
    repeatindex?: number;
    params: any;
    instanceId: string;
    moduleId: string;
    chapterId: string;
    parentKnxChapter: KnxChapterItem | undefined;
    indent: number;
    hidden: boolean;
    knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
    nesting: number;
  }) {
    var {
      args,
      repeatindex,
      title,
      data,
      parentChannel,
      params,
      nestedName,
      instanceId,
      moduleId,
      xchannels,
      chapterId,
      indent,
      nesting,
      parentKnxChapter,
      knxChannelDescriptionProperty,
      hidden,
    } = props;

    let { nestedText } = props;

    if (data.a.Layout === "Grid") {
      currentTable = new TableInfo(data.a.Id, instanceId, true, data.a.Inline ?? false);
      knxContext.currentTable = currentTable;
      knxInstanceData.setTableInfo(data.a.Id, instanceId, currentTable);
    }

    if (data.a.Layout === "Table") {
      currentTable = new TableInfo(data.a.Id, instanceId, false, data.a.Inline ?? false);
      knxContext.currentTable = currentTable;
    }

    params = data;

    //
    // apply parameterBlockRename, if exists
    //
    const parameterBlock = data;
    const rename = parameterBlockRenames.get(parameterBlock.a.Id);
    if (rename) {
      // logger.log("RENAME FOUND", data, rename);
      data.a = { ...data.a, Id: rename.Id, text: rename.Text };
    }
    /////////////////////////////////////////////////

    const hideThisChapter = data.a.Access == "None";

    nestedName += "|" + parameterBlock.a.Name;

    if (repeatindex != undefined && repeatindex >= 0) {
      nestedName += `(${repeatindex})`;
    }

    // title = knxContext.getText(parameterBlockDeref);
    title = knxContext.getTextWithTranslation(data);
    const firstNonWhiteSpaceChar = title.search(/\S|$/); // index of first non whitespace char

    parentChannel = findParentChannelByIndent(xchannels, parentChannel, firstNonWhiteSpaceChar);

    if (parentChannel) {
      knxChannelDescriptionProperty ||= parentChannel.knxChannelDescriptionProperty;
    }
    ///////////////////////////////////////////////////////////

    var textParameterRefId: string = data.a.TextParameterRefId || knxChannelDescriptionProperty?.id;
    var title0 = "";

    // if (
    //   moduleId &&
    //   textParameterRefId &&
    //   textParameterRefId.startsWith(moduleId)
    // ) {
    //   textParameterRefId = textParameterRefId.substring(moduleId.length + 1);
    // }

    var id = getId(parameterBlock);
    var xid = id;
    if (instanceId != "") {
      xid = xid + "-" + instanceId;
    }

    if (repeatindex != undefined) {
      xid += `-${repeatindex}`;
    }

    var channel = createKnxChannel(parentChannel, {
      knxChannelId: (parentChannel?.knxChannelId || "") + "_" + xid,
    });

    if (textParameterRefId) {
      title0 = knxContext.getValue(textParameterRefId, instanceId, false, true);
      if (!title0 || title0 == "-/-") {
        title0 =
          knxContext.device.template?.knxProductData.getDefaultValueForParameter1(textParameterRefId, instanceId) || "";
      }

      // knxInstanceData.addReEvalId(textParameterRefId, instanceId);

      title0 = `<%${textParameterRefId}%>`;

      knxChannelDescriptionProperty = channel.knxChannelDescriptionProperty = {
        id: textParameterRefId,
        instanceId: instanceId,
        moduleId: moduleId,
        xid: "",
      };
    }

    title = knxContext.substitutePlaceholders({
      input: title,
      args: args,
      repeatindex: repeatindex,
      params: params,
      title0: title0,
      instanceId: instanceId,
      moduleId: moduleId,
      channel: channel,
    });

    channel.subChannelIndent = firstNonWhiteSpaceChar;
    channel.knxChannelDescriptionProperty = knxContext.currentChannelDescriptionProperty;
    knxContext.currentChannelDescriptionProperty = undefined;

    title = title.trim();

    const channelName = title; // .split(":")[0].trim();

    nestedText += "|" + title; // mwe 19.6.2025: ist für (SceneProperty) Detections leider weitgehend unbrauchbar das abhängig von der Spracheinstellung

    if (lastParameterBlockChannel) {
      if (lastParameterBlockChannel.name == channelName) {
        //
        // handle: ABB SD/S4.16.1
        //
        // A: Schalten
        // A: Function
        // B: Schalten
        // B: Function
        // etc:
        //
        // we group this parameterBlocks to
        // A
        // B
        // etc.
        //

        //
        // delete "channel" and go on with lastChannel
        //
        if (xchannels[xchannels.length - 1] == channel) {
          xchannels.splice(xchannels.length - 1, 1);
        } else if (parentChannel?.subChannels) {
          if (parentChannel.subChannels[parentChannel.subChannels.length - 1] == channel) {
            parentChannel.subChannels.splice(parentChannel.subChannels.length - 1, 1);
          }

          //
          // also make lastParameterBlockChannel a top level channel !!!
          //
          if (parentChannel.subChannels[parentChannel.subChannels.length - 1] == lastParameterBlockChannel) {
            parentChannel.subChannels.splice(parentChannel.subChannels.length - 1, 1);
            xchannels.push(lastParameterBlockChannel);
          }
        }

        channel = lastParameterBlockChannel;
      }
    }

    if (channel) {
      knxChannelDescriptionProperty = channel.knxChannelDescriptionProperty || knxChannelDescriptionProperty;
    }

    channel.name = channelName;

    lastParameterBlockChannel = channel;

    /////////////////////////

    chapterId = id;
    if (instanceId) {
      chapterId += " | " + instanceId;
    }

    const knxChapterItem = new KnxChapterItem({
      device: device,
      access: parameterBlock.a.Access,
      chapterId: parameterBlock.a.Id + "-" + instanceId,
      instanceId: instanceId,
      icon: parameterBlock.a.Icon,
    });

    const Parameter = knxContext.knxProductData.getItem(parameterBlock.a.ParamRefId);

    if (!hidden && !hideThisChapter && Parameter?.a?.DisplayOrder) {
      knxChapterItem.displayOrder = parameterBlock.a.DisplayOrder;
      knxChapterItem.indent = indent;

      flatItems.push({
        id: parameterBlock.a.Id,
        type: "C",
        elm: parameterBlock,
        indent: 0,
        name: "?Header", // no itx
        order: parseInt(Parameter.a.DisplayOrder),
        knxChapterItem: knxChapterItem,
      });
    }

    if ((!hidden && !hideThisChapter) || isUiModeDeveloper()) {
      if (parentKnxChapter) {
        parentKnxChapter.subChapters.push(knxChapterItem);
      } else {
        mainKnxChapters.push(knxChapterItem);
      }
    }

    if (repeatindex != undefined) {
      chapterId += `|${repeatindex}`;
    }

    let elements: any = [];
    if (isUiModeDeveloper()) {
      elements.unshift(
        <div className="knxDebugView" style={{ marginLeft: `${whenNesting.length * 20}px` }}>
          {`ParameterBlock: ${parameterBlock.a.Id} ${title}`}
        </div>,
      );
    }

    elements = elements.concat(
      XUI({
        ...props,
        title: title,
        chapterId: chapterId,
        data: parameterBlock,
        text: parameterBlock.a.Text,
        nestedName: nestedName,
        parentChannel: channel,
        parentKnxChapter: knxChapterItem,
        xchannels: xchannels,
        indent: indent + 1,
        nesting: nesting, //  + 1,
        hidden: hideThisChapter,
      }),
    );

    if (currentTable) {
      const res = currentTable.render();
      currentTable = undefined;
      knxContext.currentTable = undefined;
      return res;
    }

    ////////////////////////////////////////////////////////////////////////
    //
    // fix ParameterBlock inside ParameterBlock GVS CHTFB-3.0/6.x.yz
    //
    for (let i in elements) {
      let element = elements[i];
      if (element && element.props?.className == "chapter") {
        elements[i] = (
          <tr>
            <td colSpan={10} className="wrapNestedParameterBlock">
              {element}
            </td>
          </tr>
        );
      }
    }
    ////////////////////////////////////////////////////////////////////////

    // console.log(`ParameterBlock check: knxInstanceData.addReEvalId(textParameterRefId, instanceId); `);

    //// MWE prüfen:    knxInstanceData.addReEvalId(textParameterRefId, instanceId);

    // see Albreceht Jung 52911ST for reference of the INLINE behaviour
    if (data.a.Inline) {
      return elements;
    }

    knxChapterItem.content = elements;

    knxChapterItem.headerData = {
      id: chapterId,
      productionHeader: title,
      debugHeader: "ParameterBlock", // no itx
      indent: firstNonWhiteSpaceChar,
      hidden: hideThisChapter,
      displayOrder: data.a.DisplayOrder,
    };

    if (!hidden) {
      if (data.a.DisplayOrder != undefined) {
        flatItems.push({
          name: title || "",
          type: "C",
          elm: <div>headerElm??</div>, // no itx
          order: parseInt(data.a.DisplayOrder),
          indent: indent,
          id: id,
        });
      }
    }

    return null;
  }

  function ChannelIndependentBlock(props: {
    data: any;
    parentKnxChapter: KnxChapterItem | undefined;
    xchannels: Array<Channel>;
  }) {
    var { data } = props;
    let elements: any = [];

    const params = data;
    // const params = data["ParameterBlock
    const parentChannel = createChannelGeneral(xchannels);

    knxContext.isChannelIndependentBlock = true;

    const knxChapterItem = new KnxChapterItem({
      device: device,
      chapterId: "Independent",
      instanceId: "",
    });

    mainKnxChapters.push(knxChapterItem);

    for (let i in data.items) {
      const item = data.items[i];
      if (isUiModeDeveloper()) {
        item._parent = data;
      }

      const xprops = {
        ...props,
        instanceId: "",
        moduleId: "",
        title: "",
        chapterId: "general", // no itx
        nestedName: "General",
        nestedText: "",
        text: "General",
        data: item,
        args: null,
        params: params,
        indent: 0,
        parentChannel: parentChannel,
        knxChannelDescriptionProperty: undefined,
        hidden: false,
        parentKnxChapter: knxChapterItem,
      };
      // logger.log(`ChannelIndependentBlock ${Tag(item)}`);
      switch (Tag(item)) {
        case noItx("ParameterBlock"):
          elements.push(ParameterBlock({ ...xprops, nesting: 0 }));
          break;

        case noItx("ParameterRefRef"):
          elements.push(ParameterRefRef({ ...xprops, knxContext }));
          break;

        case noItx("Button"):
          elements.push(KnxButton({ ...xprops, knxContext }));
          break;

        case noItx("ParameterSeparator"):
          elements.push(
            KnxParameterSeparatror({
              ...xprops,
              item,
              refId: item.a.Id,
              knxContext,
              currentTable,
            }),
          );
          break;

        case noItx("ComObjectRefRef"):
          if (true) {
            // mwe 19.12.2025, to enable signals for Gira S1     ///   isUiModeDeveloper()) {
            const res = ComObjectRefRef({ ...xprops, knxContext });
            if (res) {
              elements.push(res);
            }
          }

          break;

        case noItx("choose"):
          elements = elements.concat(Choose({ ...xprops, nesting: 0 }));
          break;

        case noItx("Module"):
          elements.push(
            Module({
              ...xprops,
              data: item,
              title: itx("knxParameterViewer.general", "Allgemein"),
              text: "General",
              nestedName: "General",
              nestedText: "",
              chapterId: "general", // no itx
              params: params,
              indent: 0,
              parentChannel: parentChannel,
              knxChannelDescriptionProperty: undefined,
              hidden: false,
              nesting: 0,
            }),
          );
          break;

        default:
          logger.error(`Unknown item in ChannelIndependentBlock ${Tag(item)}`, item);
      }
    }

    knxContext.isChannelIndependentBlock = false;

    return filterNullElements(elements);
  }

  function setDefaultsFromParameterDefinition(moduleId: string, instanceId: string, xStatic: any, args: any) {
    // return; // Disabled MWE 25.4.2024
    // wird jetzt be jedem Zugriff auf eine ParameterRef aufgerufen.
    // Dadurch sollten unbenutzte Parameter nicht mehr auf einen Wert gesetzt werden,
    // und somit "chooses" fehl schlagen,
    // so dass zB die doppeleten Text-Parameter bei MDT Tastern verschwinden
    ///////////////////////////////////////////////////////////////////////////////////////////

    // reenabled 20.9.2024
    // Die defaults fehlen offensichtlich für die Progtrammierung
    //

    //
    // set defaults from ParameterDefinitions
    //

    // return; // disable again 30.9.2024
    // enabled again 22.5.2026
    //

    const xParams = firstChildWithTag(xStatic, "ParameterRefs"); // no itx
    if (xParams) {
      for (let i in xParams.items) {
        let item = xParams.items[i];
        const id = item.a.Id;

        if (instanceId) {
          //
          //
          //
          let value = knxContext.device.template?.knxProductData.getDefaultValueForParameter(item, instanceId);

          if (item.a.BaseValue) {
            value = calcValueWithBaseValue({
              knxContext,
              data: item,
              args,
              inputNumber: 0, /// parseInt(item.a.Value),
            }).toString();
          }

          knxContext.setValueNoRerender(ValueLevels.default, value, id, instanceId, moduleId);

          if (knxContext.device.template?.knxProductData.setModuleParametersAsReferenced) {
            knxContext.knxInstanceData.setReferenced(id, instanceId); // no itx
          }

          logger.log(`setDefaultsFromParameterDefinition: ${id}=${value} instanceId=${instanceId}`);
        }

        if (item.a.InitialValue) {
          knxInstanceData.setExtendedData(
            {
              init: {
                InitialValue: item.a.InitialValue,
                Value: item.a.Value,
              },
            },
            id,
            instanceId,
          );
        }

        // if (item.a.Text && !item.a.Value) {
        //   // ignore reference with just a text field
        // } else {
        //   item = knxContext.deref(item); // mwe 10.12.2023 disabled, to get correct ui for MDT  BE-GT2Tx.01, ist anscheind eben nicht disabeld ???? darf es auch nicht ...

        //   // if (item.a.Value != undefined) {
        //   //   setMemoryInfo(id, instanceId, item, args);
        //   //   setValueNoRerender(ValueLevels.default, item.a.Value, id, instanceId, moduleId);
        //   // }
      }
    }
  }

  function ApplyModulDefinition(props: {
    title: string;
    nestedName: string;
    nestedText: string;
    chapterId: string;
    parentChannel: Channel;
    xchannels: Array<Channel>;
    moduleDefinition: any;
    args: any;
    repeatindex?: number;
    params: any;
    text: string | undefined;
    instanceId: string;
    moduleId: string;
    parentKnxChapter: KnxChapterItem | undefined;
    indent: number;
    hidden: boolean;
    knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
    nesting: number;
  }) {
    var { moduleDefinition, instanceId, args } = props;
    const dynamic = firstChildWithTag(moduleDefinition, "Dynamic"); // no itx
    const xStatic = firstChildWithTag(moduleDefinition, "Static"); // no itx

    const moduleId = moduleDefinition.a.Id;

    setDefaultsFromParameterDefinition(moduleId, instanceId, xStatic, args);

    const parameterCalculations = firstChildWithTag(xStatic, "ParameterCalculations"); // no itx
    if (parameterCalculations) {
      setupParameterCalculations(moduleId, parameterCalculations);
    }

    const paramIds = new Array<string>();
    for (let i in args) {
      const arg = args[i];
      if (arg.a && arg.a.Name) {
        paramIds.push(arg.a.RefId);
      }
    }

    const allInstanceParams = knxContext.knxInstanceData.allPropertyIds(instanceId); // no itx
    for (let i in allInstanceParams) {
      const paramId = allInstanceParams[i];
      const level = knxContext.knxInstanceData.getLevel(paramId, instanceId) || 0;
      if (level == ValueLevels.interactive || level == ValueLevels.estadaPreset) {
        // estadaPreset ist wie interactive, nur dass es nicht überschrieben wird, nur das es früher im Produkt interactiv gesetzt wurde
        paramIds.push(paramId);
      }
    }

    device.knxInstanceData.executeAllCalculationsOnce("", "", paramIds);
    device.knxInstanceData.executeAllCalculationsOnce(moduleId, instanceId, paramIds);

    if (dynamic) {
      return Dynamic({ ...props, data: dynamic, moduleId: moduleId });
    } else {
      return null;
    }
  }

  function Module(props: {
    data: any;
    parentChannel: Channel;
    xchannels: Array<Channel>;
    parentKnxChapter: KnxChapterItem | undefined;
    title: string;
    text: string | undefined;
    nestedName: string;
    nestedText: string;
    chapterId: string;
    params: any;
    indent: number;
    hidden: boolean;
    knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
    repeatindex?: number;
    nesting: number;
    instanceId: string;
  }) {
    var { data, title, indent, repeatindex, instanceId } = props;
    let id = getId(data);

    const modulDefinition = knxContext.deref(data.a.RefId);

    const args: any = [];
    for (let i in data.items) {
      const item = data.items[i];
      const tag = Tag(item);
      if (tag == "NumericArg" || tag == "TextArg") {
        args.push(item);
        var argName = item.a.Name;
        if (argName == undefined && item.a.RefId) {
          const derefArg = knxContext.deref(item);
          item.a.Name = derefArg.a.Name;
        }
        const paramRefId = item.a.RefId;
        if (paramRefId) {
          knxContext.knxInstanceData.addReEvalId(paramRefId, instanceId); // no itx
        }
      }
    }

    if (repeatindex != undefined) {
      id += `_MI-${repeatindex + 1}`;
    }

    return ApplyModulDefinition({
      ...props,
      title: title,
      instanceId: id,
      moduleDefinition: modulDefinition,
      moduleId: modulDefinition.a.Id,
      args: args,
      indent: indent,
    });
  }

  function UI_Channel(props: {
    title: string;
    nestedName: string;
    nestedText: string;
    chapterId: string;
    data: any;
    args: any;
    repeatindex?: number;
    params: any;
    instanceId: string;
    moduleId: string;
    parentKnxChapter: KnxChapterItem | undefined;
    parentChannel: Channel;
    xchannels: Array<Channel>;
    indent: number;
    hidden: boolean;
    knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
    nesting;
  }) {
    var {
      args,
      repeatindex,
      data,
      title,
      nestedName,
      params,
      instanceId,
      chapterId,
      parentChannel,
      indent,
      knxChannelDescriptionProperty,
      parentKnxChapter,
      moduleId,
      hidden,
      nesting,
    } = props;

    const saveIsChannelIndependentBlock = knxContext.isChannelIndependentBlock;
    knxContext.isChannelIndependentBlock = false;
    hasAnyChannel = true;

    var text0 = "";
    try {
      text0 = knxContext.getTextWithTranslation(data);
    } catch (ex) {}

    var titleInput = knxContext.getText(data);
    const id = getId(data);

    lastParameterBlockChannel = undefined;

    var nnnn = data.a.Name || data.a.Text;

    let channelId = device.id + "-" + instanceId + "|" + getId(data); // zurück gedreht 17.1.2026 mwe da sonst auf un zuklappen von Channels nicht funktioniert.

    // let channelId = instanceId || getId(data); // mwe 30.12.2025 im Rahmen von importSiganl Matching für ets import

    if (repeatindex != undefined) {
      channelId += `_|-${repeatindex}`;
    }

    // logger.log(`MWE2  MWE4 new Channel() ${title} ${knxContext.getText(data)}`, data);
    const channel = createKnxChannel(parentChannel, {
      knxChannelId: channelId,
      name: title,
    });

    channel.signals.items.clear();

    const TextParameterRefId = data.a.TextParameterRefId; //  = "M-0083_A-000F-32-EAAC_MD-3_P-1_R-1"
    if (channel && TextParameterRefId != undefined) {
      channel.knxChannelDescriptionProperty = {
        id: TextParameterRefId,
        instanceId: instanceId,
        moduleId: moduleId,
        xid: "",
      };

      text0 = knxContext.getValue(TextParameterRefId, instanceId, false, true);
      //knxInstanceData.addReEvalId(TextParameterRefId, instanceId);

      text0 = `<%${TextParameterRefId}%>`;

      if (text0 == undefined) {
        const text0_Default = knxProductData.getDefaultValueForParameter(
          knxProductData?.globalMap.get(TextParameterRefId),
          instanceId,
        );
        if (text0_Default != undefined) {
          title = text0_Default;
        }
      }
    }

    var title = knxContext.substitutePlaceholders({
      input: titleInput,
      args: args,
      repeatindex: repeatindex,
      params: params,
      title0: text0,
      instanceId: instanceId,
      moduleId: moduleId,

      channel: channel,
    });

    channel.name = title;
    channel.knxChannelDescriptionProperty = knxContext.currentChannelDescriptionProperty;
    knxContext.currentChannelDescriptionProperty = undefined;

    const propertyList = new Array<any>();

    const knxChapterItem = new KnxChapterItem({
      device: device,
      access: data.a.Access,
      chapterId: data.a.Id + "-" + instanceId,
      instanceId: instanceId,
      icon: data.a.Icon,
    });

    if (parentKnxChapter) {
      parentKnxChapter.subChapters.push(knxChapterItem);
    } else {
      mainKnxChapters.push(knxChapterItem);
    }

    var ui1 = XUI({
      ...props,
      title: title,
      text: data.a.Text,
      nestedName: nestedName + "|" + nnnn,
      chapterId: channelId,
      propertyList: propertyList,
      indent: indent,
      nesting: nesting + 1,
      parentKnxChapter: knxChapterItem,
      parentChannel: channel,

      knxChannelDescriptionProperty: knxChannelDescriptionProperty || channel.knxChannelDescriptionProperty,
    });

    // logger.log(`propertyList[0]: ${propertyList[0]}`);

    const firstNonWhiteSpaceChar = title.search(/\S|$/); // index of first non whitespace char
    channel.subChannelIndent = firstNonWhiteSpaceChar;

    title = title.trim();
    channel.name = title;

    if (!TextParameterRefId) {
      const id0 = propertyList[0]?.a?.Id;
      if (id0) {
        text0 = knxContext.getValue(id0, instanceId, false, true);
        title = knxContext.substitutePlaceholders({
          input: titleInput,
          args: args,
          repeatindex: repeatindex,
          params: params,
          title0: text0,
          instanceId: instanceId,
          moduleId: moduleId,

          channel: channel,
        });

        if (channel) {
          channel.knxChannelDescriptionProperty = knxContext.currentChannelDescriptionProperty;
          knxContext.currentChannelDescriptionProperty = undefined;

          channel.name = title;
        }
      }
    }

    // knxInstanceData.addReEvalId(TextParameterRefId, instanceId);

    knxContext.isChannelIndependentBlock = saveIsChannelIndependentBlock;

    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    knxChapterItem.content = ui1;
    knxChapterItem.headerData = {
      id: channelId,
      productionHeader: title,
      debugHeader: "UI_Channel", // no itx
      indent: firstNonWhiteSpaceChar,
      displayOrder: data.a.DisplayOrder,
      hidden: hidden,
    };
    return null;
  }

  function evalWhenCondition(currentValue: string, testValue: string) {
    var res = false;
    // TODO MWE 10.1.2023: hier muss man sicher noch den Type Text/Zahl unterscheiden

    const currentValueI = parseInt(currentValue);
    if (testValue.startsWith("&le;")) {
      const v = testValue.substring(4);
      res = currentValueI <= parseInt(v);
    } else if (testValue.startsWith("&ge;")) {
      const v = testValue.substring(4);
      res = currentValueI >= parseInt(v);
    } else if (testValue.startsWith("&lt;")) {
      const v = testValue.substring(4);
      res = currentValueI < parseInt(v);
    } else if (testValue.startsWith("&gt;")) {
      const v = testValue.substring(4);
      res = currentValueI > parseInt(v);
    } else if (testValue.startsWith("<=")) {
      const v = testValue.substring(2);
      res = currentValueI <= parseInt(v);
    } else if (testValue.startsWith(">=")) {
      const v = testValue.substring(2);
      res = currentValueI >= parseInt(v);
    } else if (testValue.startsWith("<")) {
      const v = testValue.substring(1);
      res = currentValueI < parseInt(v);
    } else if (testValue.startsWith(">")) {
      const v = testValue.substring(1);
      res = currentValueI > parseInt(v);
    } else if (testValue.startsWith("!=")) {
      const v = testValue.substring(2);
      res = currentValueI != parseInt(v);
    } else if (testValue.startsWith("=")) {
      const v = testValue.substring(1);
      res = currentValueI == parseInt(v);
    } else {
      const i1 = Number(currentValue);
      const i2 = Number(testValue);
      if (Number.isInteger(i1) && Number.isInteger(i2)) {
        res = i1 == i2;
      } else {
        res = currentValue == testValue;
      }
    }

    // logger.log(`evalWhenCondition ${res} ${currentValue} ${testValue} `)
    return res;
  }

  let whenNesting = new Array<any>();

  function When(props: {
    title: string;
    nestedName: string;
    nestedText: string;
    chapterId: string;
    testParam: any;
    data: any;
    parentChannel: Channel;
    xchannels: Array<Channel>;
    args: any;
    repeatindex?: number;
    params: any;
    text: string | undefined;
    instanceId: string;
    moduleId: string;
    parentKnxChapter: KnxChapterItem | undefined;
    indent: number;
    hidden: boolean;
    knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
    nesting: number;
  }): { testResult: boolean; elements: Array<any> | undefined } {
    let { title, testParam, data, args, instanceId, nestedName } = props;

    let res: { testResult: boolean; elements: Array<any> } = {
      testResult: false,
      elements: [],
    };

    const testValue = data.a.test;
    const testValues = testValue?.split(" ");
    const paramRefId = getParamRefId(testParam);

    let currentValue = knxContext.getValue(paramRefId, instanceId);

    const v1 = knxContext.knxInstanceData.getValue(paramRefId, instanceId, true);

    if (data.a?.default && `${data.a?.default}`.toLowerCase() != "false") {
      res.testResult = true;
    } else {
      if (testValue == undefined) {
        logger.error("testValue == undefined", data);
        res = { testResult: false, elements: [] };
      } else {
        res.testResult = false;
        for (let i in testValues) {
          const c1 = evalWhenCondition(currentValue, testValues[i]);
          const c2 = evalWhenCondition(v1, testValues[i]);

          res.testResult = res.testResult || c1;

          if (c1 != c2) {
            console.log("When UnreferencedParam difference: ", paramRefId, instanceId, v1, testValues[i]);
          }
        }
      }
    }

    if (res.testResult) {
      whenNesting.push(props);

      // logger.log(
      //   `MWE-C when (currentValue= [${testParam.a.ParamRefId} ${testParamDeref.a.Name} ${testParamDeref.a.Text}/ ${currentValue}] testValue=${testValue})  == ${testResult}`,
      //   whenNesting,
      // );

      const childElements = XUI({
        ...props,
        title: title, // }  | (bn=${blockName})`,
        nestedName: nestedName,
        data: data,
      });

      whenNesting.pop();

      res.elements = childElements;
    } else {
      res = { testResult: false, elements: [] };
    }

    if (isUiModeDeveloper()) {
      let testParamDeref = knxContext.deref(testParam.a.ParamRefId);

      res.elements.unshift(
        <div className="knxDebugView" style={{ marginLeft: `${whenNesting.length * 20}px` }}>
          {`When:
        testParam: ${testParam.a.ParamRefId} ${knxContext.getText(testParamDeref)} ${knxContext.getName(testParamDeref)}
        testResult: ${res.testResult}
        currentValue: ${knxContext.getValue(testParam.a.ParamRefId, instanceId)} testValue: ${testValue == undefined ? "undefined" : testValue} `}
        </div>,
      );
    }
    return res;
  }

  function Repeat(props: {
    title: string;
    nestedName: string;
    nestedText: string;
    chapterId: string;
    data: any;
    parentChannel: Channel;
    xchannels: Array<Channel>;
    args: any;
    repeatindex?: number;
    params: any;
    text: string | undefined;
    instanceId: string;
    moduleId: string;
    parentKnxChapter: KnxChapterItem | undefined;
    indent: number;
    hidden: boolean;
    knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
    nesting: number;
  }) {
    var { title, data, args, instanceId, parentChannel, params } = props;

    //    <Repeat Id="M-0083_A-0154-11-B606-O00EF_X-3" Name="" Count="64">

    const xprops = { ...props };

    let Count = 0;
    if (data.a.Count !== undefined) {
      Count = parseInt(data.a.Count);
    } else if (data.a.ParameterRefId) {
      knxInstanceData.addReEvalId(data.a.ParameterRefId, instanceId);
      Count = parseInt(knxContext.getValue(data.a.ParameterRefId, instanceId, false, true));
    }
    let elements: any = [];

    // console.log("Repeat", data.a.Id, Count);

    for (let i in data.items) {
      const item = data.items[i];

      for (let iRepeat = 0; iRepeat < Count; iRepeat++) {
        xprops.repeatindex = iRepeat;

        switch (Tag(item)) {
          case noItx("ParameterBlock"):
            elements.push(ParameterBlock({ ...xprops, nesting: 0 }));
            break;

          case noItx("ParameterRefRef"):
            elements.push(ParameterRefRef({ ...xprops, knxContext }));
            break;

          case noItx("Button"):
            elements.push(KnxButton({ ...xprops, knxContext }));
            break;

          case noItx("ParameterSeparator"):
            elements.push(
              KnxParameterSeparatror({
                ...xprops,
                item,
                refId: item.a.Id,
                knxContext,
                currentTable,
              }),
            );
            break;

          case noItx("ComObjectRefRef"):
            const res = ComObjectRefRef({ ...xprops, knxContext });

            if (isUiModeProductAdmin()) {
              if (res) {
                elements.push(res);
              }
            }

            break;

          case noItx("choose"):
            elements = elements.concat(Choose(xprops));
            break;

          case noItx("Module"):
            elements.push(
              Module({
                ...xprops,
                data: item,
                title: itx("knxParameterViewer.repeat", "Repeat"),
                text: itx("knxParameterViewer.repeat", "Repeat"),
                nestedName: itx("knxParameterViewer.repeat", "Repeat"),
                chapterId: "Repeat", // no itx
                params: params,
                indent: 0,
                parentChannel: parentChannel,
                knxChannelDescriptionProperty: undefined,
              }),
            );
            break;

          default:
            logger.error(`Unknown item in ChannelIndependentBlock ${Tag(item)}`, item);
        }

        Allocator.updateAllocatorIndexes(data.a.Id);
      }
    }
    return elements;
  }

  function Choose(props: {
    title: string;
    nestedName: string;
    nestedText: string;
    chapterId: string;
    data: any;
    parentChannel: Channel;
    xchannels: Array<Channel>;
    args: any;
    repeatindex?: number;
    params: any;
    text: string | undefined;
    instanceId: string;
    moduleId: string;
    parentKnxChapter: KnxChapterItem | undefined;
    indent: number;
    hidden: boolean;
    knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
    nesting: number;
  }) {
    var { title, data, moduleId, args, instanceId, parentChannel } = props;

    var testParam = data;

    // const ParamRefId = item.a.ParamRefId

    // var testParam = knxProductData?.globalMap.get(ParamRefId)

    // if (typeof testParam == "object" && testParam.a.RefId) {
    //     testParam = knxProductData?.globalMap.get(testParam.a.RefId)
    // }

    const savedChannelTypeProperty = knxContext.currentChannelTypeProperty;
    const savedChannelDescriptionProperty = knxContext.currentChannelDescriptionProperty;

    if (!knxContext.currentChannelTypeProperty) {
      const extData = knxInstanceData.getExtendedData(testParam.a.ParamRefId, instanceId);
      knxContext.currentChannelTypeProperty = extData?.channelTypeProperty;
    }
    if (!knxContext.currentChannelDescriptionProperty) {
      const extData = knxInstanceData.getExtendedData(testParam.a.ParamRefId, instanceId);
      knxContext.currentChannelDescriptionProperty = extData?.currentChannelDescriptionProperty;
    }

    // parentChannel.knxChannelTypeProperty = currentChannelTypeProperty;

    knxInstanceData.addReEvalId(testParam.a.ParamRefId, instanceId);

    const lastSignalsLength = xSignals.length;
    // console.log("choose lastSignalsLength", lastSignalsLength, testParam.a.ParamRefId);

    let elements: any = [];

    // search for a "default clause in the items"
    var defaultItem: any = null;
    for (let i in data.items) {
      const item = data.items[i];
      if (isUiModeDeveloper()) {
        item._parent = data;
      }

      let itemIsDefault = item.a?.default;
      if (itemIsDefault) {
        if (`${item.a?.default}`.toLowerCase() == "false") {
          itemIsDefault = false;
        }
      }

      if (Tag(item) == "when" && itemIsDefault) {
        defaultItem = item;
      }
    }

    logger.log(`Choose : ${data.a.ParamRefId}`);

    if (isUiModeDeveloper()) {
      elements.push(<div className="knxDebugView">{`Choose  ParamRefId: ${testParam.a.ParamRefId}`}</div>);
    }

    var activeWhenFound = false;

    for (let i in data.items) {
      const item = data.items[i];
      // logger.log(`Choose item: ${Tag(item)}`)
      if (isUiModeDeveloper()) {
        item._parent = data;
      }

      switch (Tag(item)) {
        case noItx("when"):
          //
          // dont eval "default when" here
          //
          if (item.a?.default == undefined || `${item.a?.default}`.toLowerCase() == "false") {
            const res = When({
              ...props,
              title: title,
              testParam: testParam,
              data: item,
            });

            if (res.testResult) {
              activeWhenFound = true;
              elements = elements.concat(res.elements);
            } else {
              if (isUiModeDeveloper()) {
                elements = elements.concat(res.elements);
              }
            }
          }

          break;
        default:
          logger.error(`Choose: Unknown child ${Tag(item)}`, data);
      }
    }

    //
    // if no when condition matches
    // use default case (if exists)
    //
    if (!activeWhenFound) {
      if (defaultItem) {
        if (isUiModeDeveloper()) {
          elements.push(<div className="knxDebugView">{`Choose: use default`}</div>);
        }
        const res = When({
          ...props,
          title: title,
          testParam: testParam,
          data: defaultItem,
        });
        elements = elements.concat(res.elements);
      } else {
        if (isUiModeDeveloper()) {
          elements.push(
            <div className="knxDebugView">
              {`Choose (NO MATCHING When, NO DEFAULT):
            ParamRefId: ${testParam.a.ParamRefId}
            instanceId: ${instanceId}
            currentValue: ${knxContext.getValue(testParam.a.ParamRefId, instanceId)}
            #data.items: ${data.items.length}
            ${data.items.map((item) => `${Tag(item)}: ${JSON.stringify(item.a)}`).join("\n")}
            `}
            </div>,
          );
        }
      }
    }

    //
    // Leave Choose block with a ChannelTypeProperty
    //
    const createDummySignals = !knxContext.isActor;
    if (knxContext.currentChannelTypeProperty && createDummySignals) {
      const signalsWithCurrentChannelTypeProperty = xSignals.filter(
        (signal) => signal.knxChannelTypeProperty == knxContext.currentChannelTypeProperty,
      );
      if (signalsWithCurrentChannelTypeProperty.length == 0 && parentChannel.name != "General") {
        //
        // ChannelTypeProperty is not used by any signal
        //
        if (xSignals.length > lastSignalsLength) {
          // but inside the choose block we have created at least one signals,
          // so we set the ChannelTypeProperty to the last signal
          //
          xSignals[xSignals.length - 1].knxChannelTypeProperty = knxContext.currentChannelTypeProperty;
          // console.log(
          //   "assign currentChannelTypeProperty",
          //   xSignals[xSignals.length - 1].number,
          //   knxContext.currentChannelTypeProperty.instanceId,
          // );
        } else {
          // lets create a dummy signal
          const dummySignal = new Signal({}, parentChannel);
          const dummySignalConfig = device?.mapChannelTypeConfig?.dummySignal;

          let dummySignalName = dummySignalConfig?.name;
          if (dummySignalName) {
            const title0 = dummySignalConfig?.textParameterId
              ? knxContext.getValue(dummySignalConfig?.textParameterId, instanceId, false, true)
              : "";

            dummySignalName = knxContext.substitutePlaceholders({
              input: dummySignalName,
              args: args,
              repeatindex: props.repeatindex,
              params: props,
              instanceId: instanceId,
              moduleId: moduleId,
              title0: title0,
            });
          } else {
            dummySignalName = parentChannel.name + noItx(": DUMMY Signal");
          }

          let dummySignalNumber = xSignals[xSignals.length - 1]?.number ?? "0";
          dummySignalNumber = (parseInt(dummySignalNumber) + 0.1).toString();

          // console.log("assign currentChannelTypeProperty create dummy signal", dummySignalNumber);

          dummySignal.knxTextENForDetection_Template = dummySignal.name = dummySignalName;
          dummySignal.knxFunctionTextEN = noItx("DUMMY");
          dummySignal.type = SignalTypes.Switch;
          dummySignal.number = dummySignalNumber;
          dummySignal.knxChannelTypeProperty = knxContext.currentChannelTypeProperty;
          xSignals.push(dummySignal);
        }
      }
    }

    // logger.log("Choose done", data, chooseNesting.length);

    knxContext.currentChannelTypeProperty = savedChannelTypeProperty;
    knxContext.currentChannelDescriptionProperty = savedChannelDescriptionProperty;

    if (isUiModeDeveloper()) {
      elements.push(<div className="knxDebugView">{`End-Choose  ParamRefId: ${testParam.a.ParamRefId}`}</div>);
    }

    return filterNullElements(elements);
  }

  function Dynamic(props: {
    title: string;
    nestedName: string;
    nestedText: string;
    chapterId: string;
    data: any;
    parentChannel: Channel;
    xchannels: Array<Channel>;
    args: any;
    repeatindex?: number;
    params: any;
    text: string | undefined;
    instanceId: string;
    moduleId: string;
    parentKnxChapter: KnxChapterItem | undefined;
    indent: number;
    hidden: boolean;
    knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
    nesting: number;
  }) {
    var { data, args, chapterId, moduleId, parentChannel } = props;
    var elements: any = [];

    for (let i in data.items) {
      const item = data.items[i];
      if (isUiModeDeveloper()) {
        item._parent = data;
      }

      switch (Tag(item)) {
        case noItx("ChannelIndependentBlock"):
          elements.push(ChannelIndependentBlock({ ...props, data: item }));
          break;

        case noItx("choose"):
          elements.push(Choose({ ...props, data: item }));
          break;

        case noItx("Channel"):
          elements.push(UI_Channel({ ...props, data: item }));
          break;

        case noItx("ParameterBlock"):
          {
            const id = getId(item);
            elements.push(
              ParameterBlock({
                ...props,
                chapterId: chapterId,

                title: "",
                data: item,
                args: args,
                nesting: 0,
              }),
            );
          }
          break;

        case noItx("Repeat"):
          elements = elements.concat(Repeat({ ...props, data: item, args: args }));

          break;

        case noItx("Module"):
          const params = data;
          elements.push(
            Module({
              ...props,
              data: item,
              title: itx("knxParameterViewer.general", "Allgemein"),
              text: "General",
              nestedName: "General",
              nestedText: "",
              chapterId: "general", // no itx
              params: params,
              indent: 0,
              parentChannel: parentChannel,
              knxChannelDescriptionProperty: undefined,
              hidden: false,
            }),
          );
          break;

        default:
          elements.push(
            <div>
              {state.translate("knxParameterViewer.dynamicUnknown", " Dynamic unknown:")} {Tag(item)} <br />
            </div>,
          );
      }
    }

    return filterNullElements(elements);
  }

  function createChannelGeneral(xchannels: Array<Channel>): Channel {
    const channelId = "general"; // no itx

    const generalJeannieChannel = createKnxChannel(undefined, {
      parentChannel: undefined,
      knxChannelId: channelId,
      name: "General",
    });
    generalJeannieChannel.channelType = ChannelType.General;
    generalJeannieChannel.signals.items.clear();
    xchannels.push(generalJeannieChannel);

    return generalJeannieChannel;
  }

  function setupParameterCalculation(moduleId: string, data: any) {
    const calculation = new KnxParameterCalculation();

    calculation.name = data.a.Name;
    calculation.language = data.a.Language;
    calculation.moduleId = moduleId;
    calculation.id = data.a.Id;

    calculation.lrTransformationFuncName = data.a.LRTransformationFunc;
    calculation.rlTransformationFuncName = data.a.RLTransformationFunc;

    calculation.lrTransformationParams = data.a.LRTransformationParameters;
    calculation.rlTransformationParams = data.a.RLTransformationParameters;

    for (let i in data.items) {
      const item = data.items[i];
      if (isUiModeDeveloper()) {
        item._parent = data;
      }

      switch (Tag(item)) {
        case noItx("RLTransformation"):
          {
            let code = item._;
            if (code) {
              code = code.replaceAll("\\n", "\n");
              if (calculation.language.toLowerCase() == "vbscript") {
                const vbscriptCode = code;
                code = transpileVbScriptToJavaScript(code);
                // logger.log(`transpile: ${calulation.name} from: \n ${vbscriptCode} to: \n ${code}`);
              }
              calculation.rlTransformation = code;
            }
          }
          break;
        case noItx("LRTransformation"):
          {
            let code: string = item._;
            if (code) {
              code = code.replaceAll("\\n", "\n");
              if (calculation.language.toLowerCase() == "vbscript") {
                const vbscriptCode = code;
                code = transpileVbScriptToJavaScript(code);
                // logger.log(`transpile: ${calulation.name} from: \n ${vbscriptCode} to: \n ${code}`);
              }
              calculation.lrTransformation = code;
            }
          }
          break;
        case noItx("LParameters"): {
          const params = data.items[i];
          for (let pi in params.items) {
            const p = params.items[pi];
            calculation.lParams.set(p.a.RefId, p.a.AliasName);

            const instanceId = ""; // calculations seem to appear only outside of modules.
            const defaultValue = knxProductData.getDefaultValueForParameter(p, instanceId);
            if (defaultValue != undefined) {
              knxContext.setValueNoRerender(ValueLevels.default, defaultValue, p.a.RefId, instanceId, moduleId);
            }
          }
          break;
        }
        case noItx("RParameters"):
          const params = data.items[i];
          for (let pi in params.items) {
            const p = params.items[pi];
            calculation.rParams.set(p.a.RefId, p.a.AliasName);

            const instanceId = ""; // calculations seem to appear only outside of modules.
            const defaultValue = knxProductData.getDefaultValueForParameter(p, instanceId);
            if (defaultValue != undefined) {
              knxContext.setValueNoRerender(ValueLevels.default, defaultValue, p.a.RefId, instanceId, moduleId);
            }
          }

          break;

        default:
          logger.error(`setupParameterCalculation: unsupported tag: ${Tag(item)}`);
          break;
      }
    }
    knxInstanceData.attachParameterCalculation(moduleId, calculation);
    return calculation;
  }

  function setupParameterCalculations(moduleId: string, data: any) {
    for (let i in data.items) {
      const item = data.items[i];
      if (isUiModeDeveloper()) {
        item._parent = data;
      }

      switch (Tag(item)) {
        case "ParameterCalculation": // no itx
          setupParameterCalculation(moduleId, item);
          break;
        default:
          logger.error(`setupParameterCalculations: unsupported tag: ${Tag(item)}`);
          break;
      }
    }
  }

  function XUI_Main(props: {
    data: any;
    parentKnxChapter: KnxChapterItem | undefined;
    xchannels: Array<Channel>;
    knxContext: KnxContext;
  }) {
    try {
      const { data, xchannels } = props;

      const elements: any = [];

      const xStatic = firstChildWithTag(data, "Static"); // no itx
      setDefaultsFromParameterDefinition("", "", xStatic, undefined);

      ////////////////////////////////////////////////////////////////////////////////
      const parameterCalculations = firstChildWithTag(xStatic, "ParameterCalculations"); // no itx
      if (parameterCalculations) {
        setupParameterCalculations("", parameterCalculations);
      }

      //////////////////////////////////////////////////////////////////////////////////////

      device.programming.initCodeSegments(xStatic);
      device.programming.createDefaultMappings();
      // device.programming.createDefaultMappingsWithActiveValues();

      const generalChannel = createChannelGeneral(xchannels);

      for (let i in data.items) {
        const item = data.items[i];
        if (isUiModeDeveloper()) {
          item._parent = data;
        }

        switch (Tag(item)) {
          case "Dynamic": // no itx
            elements.push(
              Dynamic({
                ...props,
                title: "main", // no itx
                chapterId: "main", // no itx
                text: undefined,
                nestedName: "",
                nestedText: "",
                parentChannel: generalChannel,
                instanceId: "",
                moduleId: "",
                data: item,
                args: null,
                params: null,
                indent: 0,
                knxChannelDescriptionProperty: undefined,
                hidden: false,
                nesting: 0,
              }),
            );
            break;
          default:
            logger.log(`XUI_MAIN ignored child ${Tag(item)}`); // , item, data)

          // case "St":
          //     elements.push(<Static data={item} state={state} />)
          //     break
        }
      }

      device.knxInstanceData.executeAllCalculationsOnce("", "", undefined);

      // logger.log(`XUI_MAIN done`);

      let sortByDisplayOrderNeeded = false;
      let lastDisplayOrder = -1;
      for (let i = 0; i < flatItems.length; i++) {
        let item = flatItems[i];

        if (item.order < lastDisplayOrder) {
          sortByDisplayOrderNeeded = true;
          break; // for loop
        }
        lastDisplayOrder = item.order;
      }

      if (!sortByDisplayOrderNeeded) {
        try {
          //
          // mwe 22.8.2023:
          // remove top level chapter,
          // if there is only one toplevel chaper
          //  z.B. in MDT AKD 0401.01
          //
          if (elements.length == 1 && elements[0].length == 1) {
            const x = elements[0][0]?.props?.children?.[1]?.props?.children;
            if (x?.props?.style) {
              delete x.props.style.display; // remove display:none
            }
            return x;
          }
        } catch (ex) {
          logger.error("error removing single toplevel chapter, struture of Node() changed ??");
        }
      } else {
        const sortedFlatItems = flatItems.sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0));

        structurItemsIndex = 0;
        mainKnxChapters = [];

        const structuredItems = structureItems(sortedFlatItems);
        logger.log(`structuredItems`, structuredItems);
        return structuredItems;
      }

      return elements;
    } catch (ex) {
      logger.error("Error in KnxParameterViewer", ex);
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////

  let structurItemsIndex = 0;
  function structureItems(sortedFlatItems: Array<FlatItem>): any {
    let thisKnxChapter: KnxChapterItem | undefined = undefined;

    for (let i = 0; i < sortedFlatItems.length; i++) {
      const flatItem = sortedFlatItems[i];
      logger.log(`structurItems ${i} ${flatItem.type} ${flatItem.name}`);

      if (flatItem.type == "C") {
        thisKnxChapter = flatItem.knxChapterItem;
        //       if (indent >= flatItem.indent) {

        if (thisKnxChapter) {
          thisKnxChapter.content = [];
          mainKnxChapters.push(thisKnxChapter);
        }
      } else if (thisKnxChapter) {
        thisKnxChapter.content ||= [];
        thisKnxChapter.content.push(flatItem.elm);
      }
    }
    return undefined;
  }

  function executeAssign(item: any, instanceId: string, moduleId: string) {
    // <Assign
    //     TargetParamRefRef = "M-0083_A-0090-13-9BDB_P-97_R-1034"
    //     SourceParamRefRef = "M-0083_A-0090-13-9BDB_P-97_R-1288" />
    //

    const destId = item.a.TargetParamRefRef;
    const sourceId = item.a.SourceParamRefRef;

    const sourceValue = item.a.Value;
    const sourceParamRefRef = item.a.SourceParamRefRef;

    if (sourceValue != undefined) {
      if (knxContext.currentChannelTypeProperty) {
        knxInstanceData.setExtendedData(
          { channelTypeProperty: knxContext.currentChannelTypeProperty },
          destId,
          instanceId,
        );
      }

      if (knxContext.currentChannelDescriptionProperty) {
        knxInstanceData.setExtendedData(
          { currentChannelDescriptionProperty: knxContext.currentChannelDescriptionProperty },
          destId,
          instanceId,
        );
      }

      knxContext.setValueNoRerender(ValueLevels.assign, sourceValue, destId, instanceId, moduleId);
      knxContext.knxInstanceData.setReferenced(destId, instanceId); // no itx

      // logger.log(`Assign(a) ${destId} <- ${sourceValue}`)
    } else if (sourceParamRefRef != undefined) {
      knxInstanceData.addAssign(sourceId, destId, instanceId);
      const v = knxContext.getValue(sourceParamRefRef, instanceId, false, true);
      if (v != undefined) {
        let sourceLevel = knxInstanceData.getLevel(sourceParamRefRef, instanceId) ?? ValueLevels.device;

        //
        // MWE 22.5.2026
        // "+0.1" to ensure that the assigned value wins over the default value,
        // to avoid endless recaculations because of toggle this value allways
        //
        if (sourceLevel == ValueLevels.default) {
          sourceLevel = ValueLevels.assign;
        }
        knxContext.setValueNoRerender(sourceLevel, v, destId, instanceId, moduleId);
        logger.log(`Assign(b) ${destId} <- ${sourceParamRefRef}=${v}`);

        const extData = knxInstanceData.getExtendedData(sourceParamRefRef, instanceId);
        let ccc = extData?.channelTypeProperty;
        if (ccc) {
          knxInstanceData.setExtendedData({ channelTypeProperty: ccc }, destId, instanceId);
        } // logger.log(`Assign(b) ${destId} <- ${v}`)

        knxContext.knxInstanceData.setReferenced(destId, instanceId); // no itx

        let ccc2 = extData?.ChannelDescriptionProperty;
        if (ccc2) {
          knxInstanceData.setExtendedData({ ChannelDescriptionProperty: ccc2 }, destId, instanceId);
        } // logger.log(`Assign(b) ${destId} <- ${v}`)
      } else {
        logger.error(`Assign: sourceParamRefRef not found ${sourceParamRefRef}`);
      }
    } else {
      logger.error(`Assign: unimplemented case`, item);
    }
  }

  function XUI(props: {
    title: string;
    nestedName: string;
    nestedText: string;
    chapterId: string;
    parentChannel: Channel;
    xchannels: Array<Channel>;
    data: any;
    args: any;
    repeatindex?: number;
    params: any;
    text: string | undefined;
    instanceId: string;
    moduleId: string;
    parentKnxChapter: KnxChapterItem | undefined;
    propertyList?: Array<any>;
    indent: number;
    hidden: boolean;
    nesting: number;
    knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
  }) {
    const { title, data, args, parentChannel, instanceId, moduleId, nesting } = props;
    let elements: any = [];

    if (typeof data === "string") {
      logger.error("data == string", data);
      return (
        <div>
          error string={/* no itx */}
          {data}
        </div>
      );
    }

    for (let i in data.items) {
      const item = data.items[i];
      if (isUiModeDeveloper()) {
        item._parent = data;
      }
      switch (Tag(item)) {
        case noItx("Assign"):
          executeAssign(item, instanceId, moduleId);
          break;

        case noItx("ParameterBlock"):
          elements.push(
            ParameterBlock({
              ...props,
              title: title,
              data: item,
              args: args,
              nesting: nesting,
            }),
          );
          break;

        // <ParameterBlockRename Id="M-0083_A-008B-25-AF36_PR-19" RefId="M-0083_A-008B-25-AF36_PB-1" Text="LED 1 (level 1, middle left)" />
        case noItx("ParameterBlockRename"):
          parameterBlockRenames.set(item.a.RefId, item.a);

          break;

        case noItx("ParameterRefRef"):
          elements = elements.concat(
            ParameterRefRef({
              ...props,
              data: item,
              parentChannel: parentChannel,
              knxContext,
            }),
          );
          break;

        case noItx("Button"):
          elements.push(KnxButton({ ...props, data: item, knxContext }));
          break;

        case noItx("ParameterSeparator"):
          const ps = KnxParameterSeparatror({
            ...props,
            item: item,
            refId: item.a.Id,
            parentChannel: parentChannel,
            knxContext,
            currentTable,
          });
          if (item.a.DisplayOrder) {
            flatItems.push({
              id: item.a.Id,
              type: "S",
              elm: ps,
              indent: 0,
              name: "PS", // no itx
              order: parseInt(item.a.DisplayOrder),
            });
          }
          elements = elements.concat(ps);
          break;

        case noItx("ComObjectRefRef"):
          // logger.error("ComObjectRefRef", item)

          const res = ComObjectRefRef({
            ...props,
            parentChannel: parentChannel,
            data: item,
            knxContext,
          });
          if (isUiModeDeveloper()) {
            elements.push(res);
          }
          break;

        case noItx("Channel"):
          elements.push(UI_Channel({ ...props, data: item }));
          break;

        case noItx("choose"):
          elements = elements.concat(Choose({ ...props, data: item, args: args }));

          break;

        case noItx("Repeat"):
          elements = elements.concat(Repeat({ ...props, data: item, args: args }));

          break;

        case noItx("when"):
          logger.log(`XUI Invalid when:`, item);
          elements = elements.concat(<div> {state.translate("knxParameterViewer.invalidWhen", "Invalid when")}</div>);
          break;

        case noItx("Module"):
          elements.push(Module({ ...props, data: item }));
          break;

        case noItx("Rename"):
          {
            const defItem = knxContext.deref(item);
            const thisItem = knxProductData?.globalMap.get(item.a.Id);
            logger.error("Rename not yet implemented", item.a?.Text, defItem?.a?.Text, item);
          }
          break;

        case noItx("Rows"): {
          if (currentTable) {
            for (let r = 0; r < item.items.length; r++) {
              const row = item.items[r];
              currentTable.rows.push({ text: knxContext.getText(row) });
            }
          }
          break;
        }

        case noItx("Columns"): {
          if (currentTable) {
            for (let c = 0; c < item.items.length; c++) {
              const column = item.items[c];
              currentTable.columns.push({ text: knxContext.getText(column), width: column.a.Width ?? undefined });
            }
          }
          break;
        }

        default:
          logger.error(`Unknown item in XUI ${Tag(item)}`, item);
      }
    }

    return elements;
  }

  mainKnxChapters = [];

  knxInstanceData.clearParameterCalculations();

  knxInstanceData.clearReEvalIds();

  knxInstanceData.clearTableInfos();

  ///////////////////////////////////////////////////////////////////////////////////////////////
  //
  // Render UI
  //
  ///////////////////////////////////////////////////////////////////////////////////////////////

  knxInstanceData.loadDefaultsFromConfig();

  const ui: any = [];

  const xStatic = firstChildWithTag(data, "Static"); // no itx
  const etsUiPlugin = firstChildWithTag(xStatic, "Extension")?.a?.EtsUiPlugin; // no itx

  if (etsUiPlugin) {
    const sError = state.translate(
      "knxParameterViewer.noSupportUIPlugins",
      "ESTADA supports no Knx-Productdata with UIPlugins",
    );
    logger.error(sError);

    ui.push(<div style={{ margin: "0px", padding: "10px", backgroundColor: "red" }}>{sError}</div>);
  }

  let xuiRes;

  // Some Devices (like Theben's iON 108 BK with order number 4969248)
  // have the following structure in their XML:
  //...
  // <Choose ParamRefId=X ...>
  // ...
  // <Assign TargetParamRefRef=X ...>
  // requiring multiple passes to first correctly set the assigned values
  // and then evaluate the chooses they appear in.

  /// mwe 10.6.2026: jetzt in device.ts: knxInstanceData.clearReferenced();

  knxInstanceData.clearReEvalIds();

  Allocator.resetAllocators();

  xchannels.splice(0, xchannels.length);
  xSignals.splice(0, xSignals.length);

  knxContext.rawSceneItemIndex = 0;
  knxContext.currentRawSceneData = undefined;
  knxContext.currentChannelTypeProperty = undefined;
  knxContext.currentKnxButtonSceneNumberProperty = undefined;
  knxContext.currentChannelDescriptionProperty = undefined;
  knxContext.isChannelIndependentBlock = false;

  flatItems = new Array<FlatItem>();
  knxContext.flatItems = flatItems;

  xuiRes = XUI_Main({
    data: data,
    parentKnxChapter: undefined,
    xchannels: xchannels,
    knxContext: knxContext,
  });

  ui.push(xuiRes);

  if (knxContext.isActor) {
    if (!hasAnyChannel) {
      for (const s of xSignals) {
        if (s.isChannelIndependent) {
          delete s.isChannelIndependent;
        }
      }
    }
  }

  if (mainKnxChapters.length == 1 && !mainKnxChapters[0].content) {
    mainKnxChapters = mainKnxChapters[0].subChapters;
  }

  // if (mainKnxChapters.length > 0) {
  //   device.currentKnxChapter.value = mainKnxChapters[0];
  // } else {
  //   device.currentKnxChapter.value = undefined;
  // }
  device.knxChapters = mainKnxChapters;
  //device.currentKnxChapter.value = findChapterWithProperty(device.currentKnxChapterId, mainKnxChapters);
  // Keep the previous selection until KnxChapterHierachie resolves the best matching chapter.
  // Setting undefined here causes a visible empty state flicker in KnxDetails.

  xSignals.forEach((signal) => {
    knxContext.derefKnxDescriptionProperty(signal);
  });
  return <KnxChapterHierachie device={device} knxChapterItems={mainKnxChapters} />;
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// Helper Functions
//
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function filterNullElements(a: []): any {
  return a.filter((item) => item != null && item != undefined);
}
